//
// MetadataFieldMenu
//
// Context menu for the metadata inspector

import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useStore } from '../stores'
// import { useMousePopover } from '../hooks/useMousePopover'

import { Spacer, VView } from '../appview'
import {
    Header,
    Menu,
    MenuDivider,
    MenuHeader,
    MenuItem,
    Property,
    Select,
    TextInput,
    Toggle,
} from '../components'
import { validDottedName, validName } from '../utils/text'

const ConfirmSubmenu = observer(function ConfirmSubmenu({ fieldpath, onAction }) {
    const { app } = useStore()

    const _onAction = action => {
        onAction && onAction(action)
    }

    return (
        <Menu>
            <MenuItem
                className="cc-danger"
                onClick={e => _onAction('delete_metadata_field')}
            >
                {app.text("Yes, delete metadata field '{fieldpath}'", {
                    fieldpath,
                })}
            </MenuItem>
        </Menu>
    )
})

export const MetadataFieldMenu = observer(function MetadataFieldMenu({
    item,
    field,
    fieldpath,
    onAction,
}) {
    const { app } = useStore()

    const fieldparts = fieldpath ? fieldpath.split('.') : undefined
    const original_groupname = fieldparts ? fieldparts.shift() : ''
    const original_fieldname = fieldparts ? fieldparts.join('.') : ''
    const original_fieldtype = field ? field.type : undefined
    const original_islocalized = field ? field.is_localized : undefined

    const [groupname, setGroupName] = useState(original_groupname)
    const [fieldname, setFieldName] = useState(original_fieldname)
    const [fieldtype, setFieldType] = useState(field ? field.type : 'textline')
    const [islocalized, setIsLocalized] = useState(field ? field.is_localized : false)

    const _onAction = action => {
        onAction &&
            onAction(action, {
                original_groupname,
                groupname,
                original_fieldname,
                fieldname,
                original_fieldtype,
                fieldtype,
                original_islocalized,
                islocalized,
            })
    }

    const onChangeGroupName = event => {
        setGroupName(event.target.value)
    }
    const onBlurGroupName = event => {
        setGroupName(event.target.value.trim())
    }

    const onChangeFieldName = event => {
        setFieldName(event.target.value)
    }
    const onBlurFieldName = event => {
        setFieldName(event.target.value.trim())
    }

    const onSelectFieldType = newValue => {
        setFieldType(newValue)
    }

    const onToggleIsLocalized = newValue => {
        setIsLocalized(newValue)
    }

    const has_original = original_groupname.length && original_fieldname.length
    let save_enabled =
        groupname && groupname.trim().length && fieldname && fieldname.trim().length
    let save_as_enabled =
        save_enabled &&
        ((groupname.trim() !== original_groupname && original_groupname.length) ||
            (fieldname.trim() !== original_fieldname && original_fieldname.length))
    let delete_enabled = save_enabled && !save_as_enabled && has_original

    const save_name = save_enabled ? groupname.trim() + '.' + fieldname.trim() : ''

    const header = original_fieldname.length
        ? app.text('Metadata field')
        : app.text('Add metadata field')

    const save_label = save_as_enabled
        ? app.text("Rename to '{name}'", { name: save_name })
        : save_enabled
          ? app.text("Save '{name}'", { name: save_name })
          : app.text('Save')

    const save_copy_label = save_as_enabled
        ? app.text("Save a copy as '{name}'", { name: save_name })
        : app.text('Save a copy')

    const name_exists =
        (save_as_enabled || !original_fieldname.length) && item.metadata.has(save_name)
            ? true
            : false
    if (name_exists) {
        save_enabled = false
        save_as_enabled = false
        delete_enabled = false
    }

    return (
        <VView
            className="metadatafieldmenu-panel"
            onClick={e => e.nativeEvent.stopImmediatePropagation()}
        >
            <div className="inspector">
                <Header>{header}</Header>
                <Property label={app.text('group name')}>
                    <TextInput
                        onChange={onChangeGroupName}
                        onBlur={onBlurGroupName}
                        value={groupname}
                        autoConvert={validName}
                        renderkey={'.groupname'}
                    />
                </Property>
                <Property label={app.text('field name')}>
                    <TextInput
                        onChange={onChangeFieldName}
                        onBlur={onBlurFieldName}
                        value={fieldname}
                        autoConvert={validDottedName}
                        renderkey={'.fieldname'}
                    />
                </Property>
                <Property label={app.text('field type')}>
                    <Select
                        onChange={onSelectFieldType}
                        options={{
                            textline: 'textline',
                            text: 'text',
                            textlist: 'textlist',
                            number: 'number',
                            decimal: 'decimal',
                            boolean: 'boolean',
                            image: 'image',
                            fieldpicker: 'fieldpicker',
                        }}
                        value={fieldtype}
                        renderkey={'.fieldtype'}
                    />
                </Property>
                <Property label={app.text('is localized?')}>
                    <Toggle
                        onChange={onToggleIsLocalized}
                        value={islocalized}
                        renderkey={'.islocalized'}
                    />
                </Property>
                <Spacer size={5} />
            </div>
            <Menu>
                {name_exists ? (
                    <MenuHeader className="cc-notice">
                        {app.text("Field '{name}' already in use", { name: save_name })}
                    </MenuHeader>
                ) : undefined}
                <MenuItem
                    disabled={!save_enabled}
                    onClick={e => _onAction('save_metadata_field')}
                >
                    {save_label}
                </MenuItem>
                {original_fieldname.length ? (
                    <MenuItem
                        disabled={!save_as_enabled}
                        onClick={e => _onAction('save_metadata_field_copy')}
                    >
                        {save_copy_label}
                    </MenuItem>
                ) : undefined}
                {original_fieldname.length ? (
                    <MenuItem
                        disabled={!delete_enabled}
                        className="cc-danger"
                        submenu={
                            <ConfirmSubmenu
                                fieldpath={fieldpath}
                                onAction={_onAction}
                            />
                        }
                    >
                        {app.text('Delete')}
                    </MenuItem>
                ) : undefined}
                <MenuDivider />
                <MenuItem onClick={e => _onAction('cancel')}>
                    {app.text('Cancel')}
                </MenuItem>
            </Menu>
        </VView>
    )
})
